// client/src/v2/share-card.tsx (v2 — DOM screenshot)
//
// Em vez de gerar SVG no server, captura o card REAL do DOM via html-to-image.
// Preserva a identidade visual do app (fonts, cores, decorações). Mais simples
// e mais coerente com o design existente.
//
// Pattern:
//   <div data-share-target="card" data-share-bg="ember">
//     ...conteúdo do card...
//     <ShareButton />  // este botão é escondido durante a captura
//   </div>
//
// O botão usa closest('[data-share-target]') pra achar o card pai automaticamente.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Icon } from './icons';
import { isNativeApp } from './platform';
import * as htmlToImage from 'html-to-image';

export type ShareCardType = 'quote' | 'post' | 'reading' | 'goal' | 'book';

// ─── Captura DOM → PNG ─────────────────────────────────────

// Manda log pro server (pra debug iOS WKWebView onde nao temos console acessivel)
function remoteLog(tag: string, data: any): void {
  try {
    fetch('/api/debug-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, data }),
    }).catch(() => {});
  } catch {}
}

// Espera <img> chegar em complete + decoded
async function waitImgReady(img: HTMLImageElement, timeoutMs = 4000): Promise<boolean> {
  if (img.complete && img.naturalWidth > 0) {
    try { await (img as any).decode?.(); } catch {}
    return true;
  }
  await new Promise<void>((resolve) => {
    const done = () => resolve();
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    setTimeout(done, timeoutMs);
  });
  try { await (img as any).decode?.(); } catch {}
  return img.naturalWidth > 0;
}

// Estrategia 1: canvas.drawImage(img) + toDataURL — funciona se same-origin
// ou crossOrigin=anonymous. Nao precisa de fetch.
function imgToDataUrlViaCanvas(img: HTMLImageElement): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch (e) {
    // SecurityError quando canvas tainted (cross-origin sem CORS)
    console.warn('[share] canvas tainted', e);
    return null;
  }
}

// Estrategia 2: fetch + FileReader.readAsDataURL — funciona se servidor
// permite (same-origin sempre permite).
async function imgToDataUrlViaFetch(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('[share] fetch falhou pra', url, e);
    return null;
  }
}

// Substitui <img src="dataURL"> por <div style="background-image:url(dataURL)">.
// MOTIVO: html-to-image em iOS WKWebView tem bug onde img com dataURL renderiza
// VAZIA no PNG final (mesmo com a img exibida corretamente no DOM). Workaround
// conhecido: usar div + background-image, que html-to-image inline corretamente.
// O div clonado preserva tamanho/objectFit via inline styles.
async function inlineImagesAsDataUrl(root: HTMLElement, onProgress?: (frac: number) => void): Promise<() => void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  const restores: Array<() => void> = [];

  remoteLog('share-inline', `iniciando swap de ${imgs.length} imagens (img -> div bg)`);
  console.log('[share] swapping', imgs.length, 'imagens pra div+bg');

  const total = imgs.length || 1;
  let done = 0;
  const tick = () => { done++; onProgress?.(done / total); };

  await Promise.all(imgs.map(async (img, i) => {
    const originalSrc = img.src;
    if (!originalSrc || originalSrc.startsWith('data:')) {
      remoteLog('share-inline', `img[${i}] skip — ja eh dataURL ou vazia`);
      return;
    }

    // Espera carregar
    const ready = await waitImgReady(img);
    if (!ready) {
      remoteLog('share-inline', `img[${i}] nao carregou, skip`);
      return;
    }

    let dataUrl: string | null = null;
    try { dataUrl = imgToDataUrlViaCanvas(img); } catch {}
    if (!dataUrl) dataUrl = await imgToDataUrlViaFetch(originalSrc);
    if (!dataUrl) {
      remoteLog('share-inline', `img[${i}] FALHOU obter dataURL`);
      return;
    }

    // Pega rect e estilos da img pra preservar visual no div
    const rect = img.getBoundingClientRect();
    const computed = window.getComputedStyle(img);
    const objectFit = computed.objectFit || 'cover';
    const borderRadius = computed.borderRadius || '0';
    const objectPosition = computed.objectPosition || 'center';

    // background-size mapping: object-fit cover -> cover, contain -> contain, fill -> 100% 100%
    let bgSize = 'cover';
    if (objectFit === 'contain') bgSize = 'contain';
    else if (objectFit === 'fill') bgSize = '100% 100%';

    // Cria div substituto com background-image
    const div = document.createElement('div');
    div.setAttribute('data-share-img-replacement', '1');
    div.style.cssText = [
      `width: ${img.style.width || rect.width + 'px'}`,
      `height: ${img.style.height || rect.height + 'px'}`,
      `background-image: url("${dataUrl}")`,
      `background-size: ${bgSize}`,
      `background-position: ${objectPosition}`,
      `background-repeat: no-repeat`,
      `border-radius: ${borderRadius}`,
      `display: ${computed.display === 'inline' ? 'inline-block' : computed.display}`,
    ].join('; ');

    // Inserir o div no lugar da img + esconder a img
    const parent = img.parentNode;
    if (!parent) return;
    parent.insertBefore(div, img);
    img.style.display = 'none';

    restores.push(() => {
      try { parent.removeChild(div); } catch {}
      img.style.display = '';
    });

    // CRITICO: pre-decodifica o bitmap pra garantir que o background-image
    // foi pintado no GPU ANTES do html-to-image rodar. Sem isso, a primeira
    // captura pega o div vazio (cache miss do WebKit), a segunda funciona —
    // bug exato que Wesley reportou.
    try {
      const probe = new Image();
      probe.src = dataUrl;
      // .decode() resolve so quando o bitmap esta pronto na memoria
      if (typeof (probe as any).decode === 'function') {
        await (probe as any).decode().catch(() => {});
      } else {
        await new Promise((r) => { probe.onload = r; probe.onerror = r; setTimeout(r, 800); });
      }
    } catch {}

    tick();
    remoteLog('share-inline', `img[${i}] swapped to div + decoded, dataURL=${dataUrl.length} bytes`);
  }));

  remoteLog('share-inline', 'swap concluido');
  console.log('[share] swap concluido');
  return () => restores.forEach((fn) => fn());
}

async function captureElement(el: HTMLElement, bgColor?: string, onProgress?: (step: string, frac: number) => void): Promise<Blob> {
  // Filtra fora o próprio botão de share durante a captura
  const filter = (node: HTMLElement) => {
    if (!node || !node.classList) return true;
    if (node.classList.contains('share-button-skip')) return false;
    return true;
  };

  // CRITICO: converte <img> pra <div bg-image> ANTES de capturar.
  // Cada imagem passa por pre-decode (.decode()) pra garantir bitmap pronto.
  onProgress?.('Decodificando imagens', 0);
  const restoreImages = await inlineImagesAsDataUrl(el, (frac) => {
    // 0..1 das imagens decodificadas → 0..0.5 do progresso geral.
    onProgress?.('Decodificando imagens', frac * 0.5);
  });

  try {
    // Garante paint pos-swap. Com .decode() no inline, o delay aqui pode
    // ser menor — 350ms ja basta na pratica.
    onProgress?.('Preparando layout', 0.55);
    void el.offsetHeight;
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    await new Promise(r => setTimeout(r, 350));

    const dpr = Math.max(2, Math.min(4, (window.devicePixelRatio || 2) + 1));
    const captureOpts = {
      pixelRatio: dpr,
      cacheBust: false,
      quality: 1.0,
      backgroundColor: bgColor || getComputedStyle(document.body).backgroundColor || '#161210',
      filter,
      style: {
        animation: 'none',
        transition: 'none',
        transform: 'none',
      },
      skipFonts: false,
      fontEmbedCSS: undefined,
    };

    onProgress?.('Gerando imagem', 0.7);
    let dataUrl = await htmlToImage.toPng(el, captureOpts);
    remoteLog('share-capture', `dpr=${dpr} size=${dataUrl.length}`);

    // Se PNG muito pequeno (< 120KB) o capture provavelmente perdeu imagens.
    // Tenta ate 2 retries com delay extra.
    for (let attempt = 0; attempt < 2 && dataUrl.length < 120000; attempt++) {
      onProgress?.('Refinando', 0.75 + attempt * 0.05);
      remoteLog('share-capture', `PNG ${dataUrl.length}b suspeito, retry ${attempt+1} com delay maior`);
      void el.offsetHeight;
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise(r => setTimeout(r, 700 + attempt * 300));
      dataUrl = await htmlToImage.toPng(el, captureOpts);
      remoteLog('share-capture', `retry ${attempt+1} size=${dataUrl.length}`);
    }
    onProgress?.('Finalizando', 0.95);

    console.log('[share] capture done, dpr=', dpr, 'size~', dataUrl.length);
    const res = await fetch(dataUrl);
    const rawBlob = await res.blob();
    // Pos-processa: adiciona faixa com logo + "Forjamente" embaixo da imagem.
    // Garante branding em todo card compartilhado, sem mexer no DOM original.
    try {
      const branded = await addBrandFooter(rawBlob, bgColor || '#161210');
      return branded;
    } catch (brandErr: any) {
      console.warn('[share] addBrandFooter falhou:', brandErr?.message);
      return rawBlob;
    }
  } finally {
    restoreImages();
  }
}

// Desenha o PNG capturado num canvas e acrescenta uma faixa estreita
// embaixo com a logo (icone) + texto "Forjamente". Resultado: toda imagem
// compartilhada carrega a marca de forma discreta.
async function addBrandFooter(blob: Blob, bgColor: string): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(blob);
  });

  const w = img.naturalWidth;
  const h = img.naturalHeight;
  // Faixa proporcional (~8% da altura, min 90 / max 170 px).
  const footerH = Math.max(90, Math.min(170, Math.round(h * 0.08)));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h + footerH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(img.src);
    return blob;
  }

  // Background da faixa: usa a mesma cor do card pra continuidade visual.
  ctx.fillStyle = bgColor || '#161210';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // Card original em cima
  ctx.drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);

  // Faixa: linha divisoria + logo + texto
  const isDarkBg = isHexDark(bgColor);
  const textColor = isDarkBg ? 'rgba(243,236,222,0.92)' : 'rgba(26,22,20,0.92)';
  const subColor = isDarkBg ? 'rgba(243,236,222,0.55)' : 'rgba(26,22,20,0.55)';
  const sepColor = isDarkBg ? 'rgba(243,236,222,0.10)' : 'rgba(26,22,20,0.10)';

  ctx.fillStyle = sepColor;
  ctx.fillRect(0, h, w, 1);

  // Tenta carregar a logo PNG (favicon do app); se falhar, desenha so o texto.
  try {
    const logo = await new Promise<HTMLImageElement>((resolve, reject) => {
      const l = new Image();
      l.onload = () => resolve(l);
      l.onerror = reject;
      l.src = '/icons/forjamente-icon.png';
    });
    const logoSize = Math.round(footerH * 0.60);
    const fontPx = Math.round(footerH * 0.42);
    const gap = Math.round(footerH * 0.14);
    const totalW = logoSize + gap + ctxMeasureFontWidth(ctx, 'Forjamente', fontPx);
    const startX = Math.round((w - totalW) / 2);
    const logoY = h + Math.round((footerH - logoSize) / 2);
    ctx.drawImage(logo, startX, logoY, logoSize, logoSize);

    // Texto
    ctx.font = `600 ${fontPx}px -apple-system, system-ui, sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'middle';
    ctx.fillText('Forjamente', startX + logoSize + gap, h + footerH / 2);
  } catch {
    // Sem logo — texto centralizado
    ctx.font = `600 ${Math.round(footerH * 0.48)}px -apple-system, system-ui, sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Forjamente', w / 2, h + footerH / 2);
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob null')), 'image/png');
  });
}

function ctxMeasureFontWidth(ctx: CanvasRenderingContext2D, text: string, fontPx: number): number {
  const prev = ctx.font;
  ctx.font = `600 ${fontPx}px -apple-system, system-ui, sans-serif`;
  const m = ctx.measureText(text);
  ctx.font = prev;
  return m.width;
}

function isHexDark(hex: string): boolean {
  const m = (hex || '').replace('#', '');
  if (m.length < 3) return true; // default escuro
  const expand = m.length === 3 ? m.split('').map(c => c + c).join('') : m.slice(0, 6);
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  // Luminancia perceptual rapida
  return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
}

// ─── Share via Capacitor (native) ou Web Share API ────────
// Fallback: download local do PNG (sempre funciona, ate em devices sem
// share API). User pode salvar e compartilhar manualmente.
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Salvar imagem em iOS/Android: <a download> NAO funciona em WKWebView.
// Usa Filesystem.writeFile + Share.share com files=[uri] — Apple mostra
// o share sheet com opcao "Save Image" / "Save to Photos".
async function saveImageNative(blob: Blob, fileName: string): Promise<void> {
  const fsMod: any = await import('@capacitor/filesystem').catch(() => null);
  const shareMod: any = await import('@capacitor/share').catch(() => null);
  if (!fsMod || !shareMod) throw new Error('plugins nao disponiveis');

  const arrBuf = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrBuf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  const base64 = btoa(binary);

  const fsRes = await fsMod.Filesystem.writeFile({
    path: fileName,
    data: base64,
    directory: fsMod.Directory.Cache,
  });

  // SO files — sem url. Veja comentario em shareBlob.
  await shareMod.Share.share({
    files: [fsRes.uri],
    dialogTitle: 'Salvar no dispositivo',
  });
}

async function shareBlob(blob: Blob, type: ShareCardType) {
  const fileName = `forjamente-${type}-${Date.now()}.png`;

  if (isNativeApp()) {
    try {
      // @ts-ignore — dynamic import, plugins podem não estar instalados
      const fsMod: any = await import('@capacitor/filesystem').catch(() => null);
      const shareMod: any = await import('@capacitor/share').catch(() => null);

      if (fsMod && shareMod) {
        const arrBuf = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrBuf);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        const base64 = btoa(binary);

        const fsRes = await fsMod.Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: fsMod.Directory.Cache,
        });

        // SO files — sem url nem text. iOS share sheet com url+files anexa
        // 2 itens (a img + um "link" pra mesma img). Resultado: usuario via
        // 2 anexos ao salvar/postar. So files = 1 anexo.
        await shareMod.Share.share({
          files: [fsRes.uri],
          dialogTitle: 'Compartilhar',
        });
        return;
      }
      // Fallback se filesystem não tá instalado: tenta share só com URL/texto
      if (shareMod) {
        await shareMod.Share.share({
          title: 'Forjamente',
          text: 'Veja minha leitura no Forjamente — forjamente.com.br',
          url: 'https://forjamente.com.br',
          dialogTitle: 'Compartilhar',
        });
      }
    } catch (e: any) {
      console.error('[share-native]', e?.message || e);
      throw e;
    }
  } else {
    // Web: Web Share API com File (mobile) ou download (desktop)
    const file = new File([blob], fileName, { type: 'image/png' });
    if ((navigator as any).share && (navigator as any).canShare?.({ files: [file] })) {
      try {
        await (navigator as any).share({ files: [file], title: 'Forjamente' });
        return;
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
      }
    }
    // Fallback: download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
}

// ─── Modal informativo ──────────────────────────────────────
// Sem preview, sem spinner. Modal so explica e ao apertar Compartilhar
// faz capture + share direto. PNG so existe quando precisa (durante o share).
const ShareModal: React.FC<{
  open: boolean;
  type: ShareCardType;
  targetEl: HTMLElement | null;
  bgColor?: string;
  onClose: () => void;
}> = ({ open, type, targetEl, bgColor, onClose }) => {
  const [sharing, setSharing] = useState(false);
  const [hideForCapture, setHideForCapture] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ step: string; frac: number }>({ step: '', frac: 0 });

  useEffect(() => {
    if (!open) { setError(null); setSharing(false); setHideForCapture(false); setProgress({ step: '', frac: 0 }); }
  }, [open]);

  const onShare = useCallback(async () => {
    if (!targetEl || sharing) return;
    setSharing(true); setError(null);
    setProgress({ step: 'Preparando', frac: 0.05 });
    try {
      // ESCONDE o modal completamente durante o instante da captura.
      // iOS WKWebView + html-to-image inclui backdrop/overlay na imagem capturada,
      // escurecendo o resultado. display:none + 2 frames de paint resolve.
      setHideForCapture(true);
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise(r => setTimeout(r, 100)); // garante paint sem o modal

      let blob: Blob;
      try {
        blob = await captureElement(targetEl, bgColor, (step, frac) => {
          // Progresso reportado pelo capture; UI fica oculta entao serve so
          // pra recuperar o estado ao voltar.
          setProgress({ step, frac });
        });
      } finally {
        // Mostra modal de volta imediatamente apos captura (mesmo se falhou)
        setHideForCapture(false);
      }
      setProgress({ step: 'Pronto', frac: 1 });

      await shareBlob(blob, type);
      onClose();
    } catch (e: any) {
      console.error('[share] falhou:', e);
      setError(e?.message || 'Erro ao gerar imagem');
      setHideForCapture(false);
    } finally {
      setSharing(false);
    }
  }, [targetEl, bgColor, type, sharing, onClose]);

  if (!open) return null;

  // Quando hideForCapture, modal e backdrop somem completamente do DOM render
  // pra nao serem incluidos no screenshot. setTimeout abaixo garante paint.
  const hidden = hideForCapture;

  return (
    <>
      <div onClick={sharing ? undefined : onClose} style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: sharing ? 'rgba(10,8,6,0.55)' : 'rgba(10,8,6,0.7)',
        backdropFilter: sharing ? 'none' : 'blur(8px)',
        WebkitBackdropFilter: sharing ? 'none' : 'blur(8px)',
        transition: 'background 200ms',
        display: hidden ? 'none' : 'block',
      } as React.CSSProperties}/>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9001,
        maxWidth: 480, margin: '0 auto',
        background: 'var(--paper)', borderTopLeftRadius: 28, borderTopRightRadius: 28,
        padding: '14px 20px calc(36px + env(safe-area-inset-bottom, 0px))',
        maxHeight: '92vh', overflow: 'auto',
        display: hidden ? 'none' : 'block',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--ink-4)', opacity: 0.3, margin: '0 auto 18px' }}/>

        <div className="serif" style={{ fontSize: 26, marginBottom: 12, textAlign: 'center' }}>
          Compartilhar este card
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', textAlign: 'center', marginBottom: 8, lineHeight: 1.5 }}>
          Vamos gerar uma imagem deste card e abrir o menu de compartilhamento do seu celular.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', marginBottom: 24, lineHeight: 1.5 }}>
          De lá você escolhe: <strong style={{ color: 'var(--ember)' }}>Stories do Instagram</strong>, <strong style={{ color: 'var(--ember)' }}>Status do WhatsApp</strong>, salvar na galeria, ou qualquer outro app.
        </div>

        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
            fontSize: 13, textAlign: 'center', marginBottom: 16,
          }}>{error}</div>
        )}

        {/* Barra de progresso real — aparece enquanto sharing. Reflete os
            estados reportados pelo capture (decode/paint/render). */}
        {sharing && (
          <div style={{ marginBottom: 16 }}>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', textAlign: 'center', marginBottom: 8,
            }}>
              {progress.step || 'Processando'} · {Math.round(progress.frac * 100)}%
            </div>
            <div style={{
              height: 4, borderRadius: 2, background: 'var(--paper-3)',
              overflow: 'hidden', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${Math.max(4, progress.frac * 100)}%`,
                background: 'var(--ember)', borderRadius: 2,
                transition: 'width 200ms ease-out',
                boxShadow: '0 0 8px rgba(199,83,26,0.4)',
              }}/>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={sharing} className="btn btn-ghost" style={{ flex: 1, padding: '14px', opacity: sharing ? 0.5 : 1 }}>
            Cancelar
          </button>
          <button
            onClick={onShare}
            className="btn btn-ember"
            disabled={sharing || !targetEl}
            style={{ flex: 2, padding: '14px', opacity: (sharing || !targetEl) ? 0.7 : 1 }}
          >
            {sharing ? 'Gerando imagem…' : 'Compartilhar'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Botão pequeno (ícone) ──────────────────────────────
export const ShareButton: React.FC<{
  type: ShareCardType;
  size?: number;
  variant?: 'icon' | 'icon-soft' | 'icon-tiny';
  title?: string;
}> = ({ type, size = 18, variant = 'icon-soft', title = 'Compartilhar' }) => {
  const [open, setOpen] = useState(false);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [bgColor, setBgColor] = useState<string | undefined>(undefined);
  const btnRef = useRef<HTMLButtonElement>(null);

  const onClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const card = (e.currentTarget as HTMLElement).closest('[data-share-target]') as HTMLElement | null;
    if (!card) {
      console.error('[share] nao achou parent com data-share-target');
      return;
    }
    setTargetEl(card);
    setBgColor(card.getAttribute('data-share-bg') || undefined);
    setOpen(true);
  }, []);

  const styles: Record<typeof variant, React.CSSProperties> = {
    icon: {
      width: size + 18, height: size + 18, borderRadius: 999,
      background: 'var(--paper-3)', border: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    'icon-soft': {
      width: size + 18, height: size + 18, borderRadius: 999,
      background: 'rgba(232,122,59,0.12)', border: '1px solid rgba(232,122,59,0.25)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    'icon-tiny': {
      width: size + 10, height: size + 10, borderRadius: 999,
      background: 'transparent', border: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={onClick}
        title={title}
        aria-label={title}
        className="share-button-skip"
        style={styles[variant]}
      >
        <Icon name="share" size={size} color="var(--ember)" stroke={2}/>
      </button>
      <ShareModal
        open={open}
        type={type}
        targetEl={targetEl}
        bgColor={bgColor}
        onClose={() => { setOpen(false); setTargetEl(null); }}
      />
    </>
  );
};
